import {
  keepPreviousData,
  useMutation,
  useQuery,
  type UseQueryOptions
} from '@tanstack/react-query'

import { skidsService } from './skids-service'
import type { SkidPaginatedResponse, SkidPayload, SkidUpdatePayload, SkidsQueryParams } from './skids-types'

export const useSkids = (
  params: SkidsQueryParams = {},
  options?: Pick<UseQueryOptions<SkidPaginatedResponse>, 'enabled'>
) => {
  return useQuery({
    queryKey: ['skids', params],
    queryFn: () => skidsService.get(params),
    // Keep showing the previous list during refetch so cache invalidations
    // from bundle/skid mutations don't drop consumers (e.g. MultiOrderSkidsBar)
    // back to the Skeleton state on every scan. Initial mount still shows
    // Skeleton because there's no previous data.
    placeholderData: keepPreviousData,
    ...options
  })
}

export const useSkid = (id: number | null | undefined) => {
  return useQuery({
    queryKey: ['skids', 'detail', id],
    queryFn: () => skidsService.getById(id!),
    enabled: id != null
  })
}

export const useCreateSkid = () => {
  return useMutation({
    mutationFn: (payload: SkidPayload) => skidsService.add(payload),
    meta: {
      successMessage: 'Skid created successfully',
      invalidatesQuery: ['skids', 'boxes', 'orders', 'items']
    }
  })
}

export const useUpdateSkid = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: SkidUpdatePayload }) =>
      skidsService.update(id, payload),
    meta: {
      successMessage: 'Skid updated successfully',
      invalidatesQuery: ['skids', 'boxes', 'orders', 'items']
    }
  })
}

export const useDeleteSkid = () => {
  return useMutation({
    mutationFn: (id: number) => skidsService.delete(id),
    meta: {
      successMessage: 'Skid deleted successfully',
      invalidatesQuery: ['skids', 'boxes', 'orders', 'items']
    }
  })
}
